import { useEffect, useCallback, useMemo, useRef, useState, Fragment, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { Quote } from '../data'
import type { BookingTranslationKey } from '../config'
import {
  buildBreakdownRows,
  BUCKET_GROUP_LABELS,
  type PriceBucket,
  type ResolvedPriceBreakdownConfig,
} from '../../../common/price-breakdown/breakdown'

interface PriceDetailsModalProps {
  ratePlan: Quote['rateplans'][0]
  formatPrice: (amount: number) => string
  showSubtotal?: boolean
  // Leaves optional (#1191): add-row drafts and deepPartial feed-global rows
  // can be one-sided; a row without `from` never matches, without `to` keeps
  // the original title.
  labelReplacements?: { from?: string; to?: string }[]
  /** Resolved Inline Price Breakdown config (#1064 Wave 3), or null when the
   * feature is off. The modal honors ONLY its explanations and grouped-tax
   * display — style/position/grouping are inline-surface concerns, and the
   * modal stays the always-itemized full-detail surface either way. */
  priceBreakdown?: ResolvedPriceBreakdownConfig | null
  onClose: () => void
  /** #1424: this modal's own chrome text (Subtotal:/Total: row labels, the
   *  close button and per-charge explanation-toggle aria-labels). The bucket
   *  group labels (Rent/Fees/Add-ons/…) and any row.label/tax label already
   *  come from the shared common/price-breakdown/breakdown.ts module (used by
   *  checkout/confirmation too) or from the operator's own taxLabel setting —
   *  out of this widget-local table, same as every other pre-existing
   *  operator-configurable string field. */
  _t: (key: BookingTranslationKey, defaultValue?: string) => string
}

export default function PriceDetailsModal({ ratePlan, formatPrice, showSubtotal = true, labelReplacements = [], priceBreakdown = null, onClose, _t }: PriceDetailsModalProps) {
  // Relabel invoice-item titles per the admin's replacement rules (WP:
  // price_details_text_replacements). Exact-match on the original title; a
  // rule applies only when its `to` is a non-blank string — the save-path
  // strip keeps in-progress one-sided rows, and a blank `to` must never
  // erase the charge title.
  const relabel = (title: string) => {
    const to = labelReplacements.find(r => r.from === title)?.to
    return typeof to === 'string' && to.trim() !== '' ? to : title
  }
  // Close on Escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose()
  }, [onClose])

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // #919: this overlay is hand-rolled on purpose (it must stay clickable
  // outside a Radix modal — see the priceFromModalWidget note in
  // BookingForm), so it gets the modal behaviour the two Radix dialogs get
  // for free: initial focus on the close control, focus back to the opener
  // on close, a body scroll lock, and a Tab trap (below).
  const contentRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    const content = contentRef.current
    if (!content) return
    // Inside a shadow root document.activeElement is the host; ask the root.
    const root = content.getRootNode() as Document | ShadowRoot
    const opener = (root.activeElement ?? document.activeElement) as HTMLElement | null
    closeRef.current?.focus()
    // body.style.overflow is a shared global: another modal (a second
    // widget, the host page's own dialog) may already hold the lock, and one
    // may take it while this is open. Only lock when nobody has, and only
    // release when the lock is still ours — never restore over someone else.
    const body = document.body
    const prevOverflow = body.style.overflow
    const ownsLock = prevOverflow !== 'hidden'
    if (ownsLock) body.style.overflow = 'hidden'
    return () => {
      if (ownsLock && body.style.overflow === 'hidden') body.style.overflow = prevOverflow
      opener?.focus?.()
    }
  }, [])

  const trapTab = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return
    const content = contentRef.current
    if (!content) return
    const items = Array.from(content.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ))
    if (items.length === 0) return
    const first = items[0]
    const last = items[items.length - 1]
    const active = (content.getRootNode() as Document | ShadowRoot).activeElement
    // Focus outside the dialog (script, AT, another overlay): a Tab in either
    // direction re-enters at the matching end instead of walking the page.
    if (!content.contains(active)) {
      e.preventDefault()
      ;(e.shiftKey ? last : first).focus()
    } else if (e.shiftKey && active === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus()
    }
  }

  // Modal-local disclosure state; a reopened modal starting fresh is fine —
  // it is the inline breakdown whose state must survive re-renders.
  const [expOpen, setExpOpen] = useState<Record<string, boolean>>({})
  const [taxOpen, setTaxOpen] = useState(false)

  // The Wave 3 shared row model, in the modal's presentation order (bucket
  // sections, matching WP plugin grouping — the modal has always rendered
  // this order; the builder is now the single source of the bucketing).
  // Always itemized with every bucket visible; `rentStyle: 'title'` keeps
  // rent rows on their own names (per-night math is inline-surface-only);
  // explanations and the grouped-tax cluster light up only when the operator
  // configured them on the Inline Price Breakdown.
  const rows = useMemo(() => buildBreakdownRows(ratePlan.invoiceItems, {
    grouping: 'itemized',
    rentStyle: 'title',
    showTaxes: true,
    showDeposit: true,
    taxDisplay: priceBreakdown?.taxDisplay ?? 'itemized',
    taxLabel: priceBreakdown?.taxLabel ?? 'Taxes',
    explanations: priceBreakdown?.explanations ?? {},
    nights: 0,
    order: 'bucketed',
  }), [ratePlan, priceBreakdown])

  const subTotal = ratePlan.total - ratePlan.totalTaxes

  // The Subtotal row sits between the charges and the taxes. Tax rows are
  // always last in 'bucketed' order, so it slots in before the first of them
  // (or before the Total when there are none).
  const firstTaxIndex = rows.findIndex(
    (row) => row.kind === 'taxCluster' || (row.kind === 'item' && row.bucket === 'tax'),
  )
  const subtotalAt = firstTaxIndex === -1 ? rows.length : firstTaxIndex

  // First-row-of-bucket carries the group label in the left column, exactly
  // as the modal has always rendered.
  const seenBuckets = new Set<string>()
  const bucketLabel = (bucket: PriceBucket | 'unknown') => {
    if (seenBuckets.has(bucket)) return ''
    seenBuckets.add(bucket)
    if (bucket === 'unknown') return ''
    // Literal keys (not a lookup table) so the dead-setting sentinel sees each one used.
    switch (bucket) {
      case 'rent': return _t('group_rent', BUCKET_GROUP_LABELS.rent)
      case 'fee': return _t('group_fees', BUCKET_GROUP_LABELS.fee)
      case 'manual': return _t('group_addons', BUCKET_GROUP_LABELS.manual)
      case 'refundable': return _t('group_others', BUCKET_GROUP_LABELS.refundable)
      case 'tax': return _t('group_taxes', BUCKET_GROUP_LABELS.tax)
    }
  }

  const subtotalRow = showSubtotal && subTotal > 0 && (
    <tr className="hk-background-muted">
      <td className="hk-text-bold">{_t('label_subtotal')}</td>
      <td></td>
      <td className="hk-text-right hk-text-bold">{formatPrice(subTotal)}</td>
    </tr>
  )

  const explanationToggle = (expKey: string, explanation: string | null) => explanation ? (
    <button
      type="button"
      className="hr-pb-exp-toggle"
      aria-expanded={expOpen[expKey] === true ? 'true' : 'false'}
      aria-label={_t('aria_charge_explanation')}
      onClick={() => setExpOpen((m) => ({ ...m, [expKey]: !(m[expKey] === true) }))}
    >
      <span className="hr-pb-exp-icon" aria-hidden="true">i</span>
    </button>
  ) : null

  const explanationRow = (expKey: string, explanation: string | null) =>
    explanation && expOpen[expKey] === true ? (
      <tr className="price-details-explanation">
        <td></td>
        {/* The shared `.hr-pb-explanation` sets `display: grid`, which takes the
            cell out of the table formatting model — the browser then wraps it in
            an anonymous cell and IGNORES colSpan, squeezing the text into the
            middle column of a 3-column table. Keep the cell a real table-cell
            and put the grid on an inner element. */}
        <td colSpan={2}>
          <div className="hr-pb-explanation" data-open="true"><div>{explanation}</div></div>
        </td>
      </tr>
    ) : null

  return (
    <div
      id="homelocal-booking-price-details-modal"
      className="booking-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      onKeyDown={trapTab}
    >
      <div
        ref={contentRef}
        className="booking-modal-content booking-modal-content--narrow"
        role="dialog"
        aria-modal="true"
        aria-label={_t('aria_price_details_dialog')}
      >
        <button ref={closeRef} type="button" className="booking-modal-close" onClick={onClose} aria-label={_t('aria_close_price_details')}>&times;</button>

        <table className="price-details-table">
          <tbody>
            {rows.map((row, i) => {
              const before = i === subtotalAt ? subtotalRow : null
              if (row.kind === 'taxCluster') {
                return (
                  <Fragment key={`row-${i}`}>
                    {before}
                    <tr>
                      <td>{bucketLabel('tax')}</td>
                      <td>
                        <button
                          type="button"
                          className="hr-pb-tax-toggle"
                          aria-expanded={taxOpen ? 'true' : 'false'}
                          onClick={() => setTaxOpen((o) => !o)}
                        >
                          {row.label}
                          <span className="hr-pb-tax-caret" aria-hidden="true" />
                        </button>
                      </td>
                      <td className="hk-text-right">{formatPrice(row.total)}</td>
                    </tr>
                    {taxOpen && row.items.map((tax, j) => (
                      <Fragment key={`tax-${j}`}>
                        <tr className="price-details-tax-line">
                          <td></td>
                          <td>{relabel(tax.title)}{explanationToggle(tax.expKey, tax.explanation)}</td>
                          <td className="hk-text-right">{formatPrice(tax.amount)}</td>
                        </tr>
                        {explanationRow(tax.expKey, tax.explanation)}
                      </Fragment>
                    ))}
                  </Fragment>
                )
              }
              if (row.kind !== 'item') return null // grouped rows never occur here
              return (
                <Fragment key={`row-${i}`}>
                  {before}
                  <tr>
                    <td>{bucketLabel(row.bucket)}</td>
                    <td>{relabel(row.title)}{explanationToggle(row.expKey, row.explanation)}</td>
                    <td className="hk-text-right">{formatPrice(row.amount)}</td>
                  </tr>
                  {explanationRow(row.expKey, row.explanation)}
                </Fragment>
              )
            })}

            {/* Subtotal lands at the very end of the charge list when no tax
                row rendered to anchor it. */}
            {subtotalAt === rows.length && subtotalRow}

            {/* Total row */}
            <tr className="hk-background-muted">
              <td className="hk-text-bold">{_t('label_total_modal')}</td>
              <td></td>
              <td className="hk-text-right hk-text-bold">{formatPrice(ratePlan.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
